/**
 * THE MONTH, AS SCHEDULE'S MONTH VIEW LAYS IT OUT, which no artboard draws: a month in words,
 * "October 2026", over weeks of seven days that start on Sunday, as a Florida wall calendar does,
 * with nothing drawn before the first day or after the last; the months she may step across; and
 * the mark each of ops's statuses draws on a day.
 *
 * - THE BUSINESS'S DAYS. The places give a day as `YYYY-MM-DD`, already the business's own day
 *   (the Worker's `lib/localday.ts`), so a month is made from the calendar alone, in UTC, which has
 *   no clock changes, and never from the phone's clock or its time zone (`dates.ts` says why).
 * - THE ARROWS STEP ONE MONTH AT A TIME, from the month of the earliest visit in her copy to the
 *   month of the last in the horizon, and always across today's month, where the view opens. A
 *   month between two that hold visits is stepped through, never jumped, so a step is always one.
 * - A MARK FOR EACH STATE, never a colour alone (`components/Month.tsx` draws them): planned and
 *   confirmed share one, since both are a visit ops has on the calendar and neither says more; a
 *   clean under way, one done, one skipped or cancelled and one the cleaner could not get into
 *   each have their own. A status this build does not know draws no state at all, rather than a
 *   wrong one.
 * - A DAY'S MARKS IN A FIXED ORDER, what is under way or still to come before what is over
 *   (`DAY_ORDER`), so the first, which colours the day, never says a day is over while a visit on
 *   it is still to come.
 *
 * Anything that is not a real day or month gives nothing, and the view draws nothing for it.
 */
import { MONTHS, WEEKDAYS } from './dates.ts';

/** The mark a visit's status draws on its day. */
export type Mark = 'planned' | 'underWay' | 'done' | 'cancelled' | 'noAccess';

/** The marks in the order the key names them. */
export const MARKS: readonly Mark[] = ['planned', 'underWay', 'done', 'cancelled', 'noAccess'];

const BY_STATUS: ReadonlyMap<string, Mark> = new Map([
  ['planned', 'planned'],
  ['confirmed', 'planned'],
  ['in_progress', 'underWay'],
  ['done', 'done'],
  ['cancelled', 'cancelled'],
  ['no_access', 'noAccess'],
]);

/** The mark ops's status draws, or null for a status this build does not know, or none. */
export function markOf(status: string | undefined): Mark | null {
  return status === undefined ? null : (BY_STATUS.get(status) ?? null);
}

/**
 * The order a day's marks are drawn in, and the first of them colours the day: a clean under way,
 * then a visit planned, one the cleaner could not get into, a clean done, and a visit skipped or
 * cancelled last. So a day reads skipped only when every visit on it was, and done only when nothing
 * on it is still to come, never by whichever visit the copy sorts first (the replay of the owner's
 * walk, 2026-10-17, found a skipped first visit greying a day whose other homes were still coming).
 */
export const DAY_ORDER: readonly Mark[] = ['underWay', 'planned', 'noAccess', 'done', 'cancelled'];

/** Where a mark stands in `DAY_ORDER`; a status this build does not know after every known one. */
const placeOf = (mark: Mark | null): number =>
  mark === null ? DAY_ORDER.length : DAY_ORDER.indexOf(mark);

/**
 * A day's marks in `DAY_ORDER`, those of one state in the order given, as a new list. The first is
 * the one the day is coloured by.
 */
export function inDayOrder(marks: readonly (Mark | null)[]): (Mark | null)[] {
  return [...marks].sort((one, other) => placeOf(one) - placeOf(other));
}

/** The heads of the month's seven columns, Sunday first, each short as drawn and whole as read. */
export const WEEK_HEADS: readonly { readonly short: string; readonly long: string }[] =
  WEEKDAYS.map((long) => ({ short: long.slice(0, 3), long }));

const DAY_MS = 24 * 60 * 60 * 1000;

/** A `YYYY-MM` month as its year and its month, 1 to 12, or null when it is not a real month. */
function partsOf(month: string): { readonly year: number; readonly month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const [year, number] = [Number(match[1]), Number(match[2])];
  return number >= 1 && number <= 12 ? { year, month: number } : null;
}

/** A year and a month, 1 to 12, back as `YYYY-MM`. */
const monthKey = (year: number, month: number) =>
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;

/** The month a `YYYY-MM-DD` day is in, `YYYY-MM`, or null when it is not a real day. */
export function monthOf(day: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const calendar = new Date(Date.UTC(year, month - 1, date));
  const real =
    calendar.getUTCFullYear() === year &&
    calendar.getUTCMonth() === month - 1 &&
    calendar.getUTCDate() === date;
  return real ? monthKey(year, month) : null;
}

/** A `YYYY-MM` month in words with its year, "October 2026", or null when it is not a month. */
export function monthInWords(month: string): string | null {
  const parts = partsOf(month);
  return parts === null ? null : `${MONTHS[parts.month - 1]} ${parts.year}`;
}

/** The month `by` months after this one, or before it when `by` is less than nought. */
export function stepMonth(month: string, by: number): string | null {
  const parts = partsOf(month);
  if (parts === null) return null;
  const index = parts.year * 12 + (parts.month - 1) + by;
  return monthKey(Math.floor(index / 12), (index % 12) + 1);
}

/**
 * The month's weeks, Sunday first, each of seven days as `YYYY-MM-DD`, with null where the week
 * runs before the first of the month or after its last. Four to six weeks; none for a month that is
 * not a real one.
 */
export function monthWeeks(month: string): (string | null)[][] {
  const parts = partsOf(month);
  if (parts === null) return [];
  const first = new Date(Date.UTC(parts.year, parts.month - 1, 1));
  const length = new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from<string | null>({ length: first.getUTCDay() }).fill(
    null,
  );
  for (let date = 0; date < length; date += 1) {
    cells.push(new Date(first.getTime() + date * DAY_MS).toISOString().slice(0, 10));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let start = 0; start < cells.length; start += 7) weeks.push(cells.slice(start, start + 7));
  return weeks;
}

/** The first and the last month the arrows reach, both `YYYY-MM`. */
export interface MonthBounds {
  readonly first: string;
  readonly last: string;
}

/**
 * The months the arrows reach: from the month of the earliest of `days` to the month of the last,
 * and always today's, where the view opens. A day that is not a real one is left out. Null when
 * today is not a real day, and the view is not drawn.
 */
export function monthBounds(days: readonly string[], today: string): MonthBounds | null {
  const now = monthOf(today);
  if (now === null) return null;
  let first = now;
  let last = now;
  for (const day of days) {
    const month = monthOf(day);
    if (month === null) continue;
    if (month < first) first = month;
    if (month > last) last = month;
  }
  return { first, last };
}

/** The month one step from this one inside the bounds, or null where the arrow reaches no further. */
export function stepWithin(month: string, by: -1 | 1, bounds: MonthBounds): string | null {
  const next = stepMonth(month, by);
  return next === null || next < bounds.first || next > bounds.last ? null : next;
}
